import {TextEncoder} from "encoding";
import Bottleneck from "bottleneck";
import * as usbutil from "../usbutil.js";
import I2C from "i2c";
import sleep from "sleep";
import semver from "semver";
import Device from "../device.js";

const ERROR_FLAGS = [
  "Watchdog System Reset",
  "Brown-out Reset",
  "External Reset",
  "Clock Failure",
  "Servo#1 No Feedback Signal",
  "Servo#2 No Feedback Signal",
  "Servo#3 No Feedback Signal",
  "Servo#4 No Feedback Signal"
];


const TIMEOUT = 500;

class Module extends Device {
  static constructor() {
    /* 
      each instance of this pump communicates on the same i2c chip address
      so the limiter must be on class level NOT on instance level
    */
    Module.limiter = new Bottleneck({
      maxConcurrent: 1,
      minTime: 10
    });          
  }
  constructor(i2cBus, usbProductName, usbSerialNumber) {       
    super(i2cBus, usbProductName, usbSerialNumber);
    
    if (i2cBus instanceof I2C)
      this.__i2cBus = i2cBus;
    else 
      throw TypeError("invalid bus type");

    this.id = "";
    this.status = "uninitialized";
    this.__usbProductName = usbProductName;
    this.__usbSerialNumber = usbSerialNumber;
    this.rpm = 0;
    this.mlh = 0;
    this.sumML = 0;    

    this.__pumpid = usbSerialNumber;
       
  }
  async initialize() {
    try {
      this.id = await this.command("GetInfo");
      await this.command(`SetRPM${this.__pumpid} 0`);
      this.rpm = 0;
      this.mlh = 0;
      this.calibrationStatus = await this.command(`GetCal${this.__pumpid}`);
      this.status = "OK";
      
      this.usbProductName = this.__usbProductName;
	  this.usbSerialNumber = this.__usbSerialNumber;
      this.name = {
        "1": "parallaxPumpAntifoam",
        "2": "parallaxPumpBase",
        "3": "parallaxPumpAcid",
        "4": "parallaxPumpFeed"
      }[this.usbSerialNumber];
            
      this.firmwareVersion = await this.getFirmwareVersion();

      setInterval(this.__ontimeout.bind(this), 1000);

      jfermi.addEventListener("fermentationstart", this.#fermentationstart_handler.bind(this));    

      /* publicate remote methods to clients */
      jfermi[`RM_${this.usbProductName}_${this.usbSerialNumber}_calibrate`] = this.RM_parallaxpump_calibrate.bind(this);
      jfermi[`RM_${this.usbProductName}_${this.usbSerialNumber}_setRPM`] = this.RM_parallaxpump_setRPM.bind(this);
      jfermi[`RM_${this.usbProductName}_${this.usbSerialNumber}_setMLH`] = this.RM_parallaxpump_setMLH.bind(this);          
      jfermi[`RM_${this.usbProductName}_${this.usbSerialNumber}_clearsum`] = this.RM_parallaxpump_clearsum.bind(this);                
    } catch(e) {
      this.id = "-";
      this.status = "Failed: "+e.message;
      throw e;
    }
  }
  /* when the fermentation stops we reset the summa value */
  #fermentationstart_handler() {    
    this.clearSum();
  }   
  async getFirmwareVersion() {
    var versionString = await this.command("GetFirmwareVersion");
    if (!versionString.startsWith("OK")) return "1.0.0";   
    versionString = versionString.replace("OK ", "");
    return versionString;
  }  
  async getErrorFlags() {
    let errorFlags= await this.command("GetErrorFlags");
    errorFlags = errorFlags.replace("OK ", "");    
    errorFlags = parseInt( errorFlags );
    return errorFlags;
  }    
  async update() {
    try {          
      if (semver.compare(this.firmwareVersion, "2.0.0")>=0) {
        let errorFlags = await this.getErrorFlags();
        this.errors = ERROR_FLAGS.filter((e,i) => errorFlags&i);
      }      
      this.status = "Ok";
    } catch(e) {
      this.status = "Failed";
      throw e;
    }      
  }
  /* sending new command to the device while waiting for the response may cause concurrency problems           */
  /* so we wrapped the function and limited the maximum number of concurrent calls to 1 with bottlenck library */  
  async command(command) {   
    return await Module.limiter.schedule(() => this.command_wrapped(command));            
  } 
  async command_wrapped(command) {        
    var promiseCommand = new Promise(async function _(resolve, reject) {      
      await this.__i2cBus.write( new TextEncoder().encode(command+"\0") );
      await sleep(100);
      
      /* 
      	https://forums.raspberrypi.com/viewtopic.php?t=170958
        
		Fast repetitive reads using Linux OS does seem to cause i2c issues.
		Most of my code was for detecting missing data and handling errors.
		Went as low as 10KHz i2c clock rates, which helped.      
      */
      
      //TODO: var result = await this.__i2cBus.read(31);
      //var uint8array = new Uint8Array(100);
      //let byte, i=0;
      //do {                
      //  uint8array[i] = (new Uint8Array( await this.__i2cBus.read(1) ))[0];
      //} while(byte!=0 && ++i<100)
      
      var result = await this.__i2cBus.read(100);      
      var uint8array = new Uint8Array(result);     
      uint8array = uint8array.filter(v => v != 0);
      if (uint8array[0]==0) {
        reject( new Error("no pending request") );
        return;
      }
      var string = String.fromCharCode.apply(null, uint8array);      
      if (string.startsWith("ERR "))
        reject( new Error (string.replace("ERR ", "")) );
      else
        resolve(string);      
    }.bind(this));

    var promiseTimeout = new Promise(function(resolve, reject) {
      setTimeout(function() {   
        reject(new Error("Timeout"));
      }, TIMEOUT);
    }.bind(this));       


    return await Promise.race([promiseCommand, promiseTimeout]);    
  }
  clearSum() {
    this.sumML = 0;    
  } 
  async setRPM(value) {
    let result = await this.command(`SetRPM${this.__pumpid} ${value}`);
    this.rpm = value;
    return result;
  }
  async setMLH(value) {
    let result = await this.command(`SetMLH${this.__pumpid} ${value}`);
    this.mlh = value;
    return result;
  }  
  async calibrate(rpm, mlh) {
    await this.command(`SetCal${this.__pumpid},${rpm},${mlh}`);
    this.calibrationStatus = await this.command(`GetCal${this.__pumpid}`);      
  }
  async RM_parallaxpump_calibrate(request, rpm, mlh) {    
    try {            
      await this.calibrate(rpm, mlh);
      console.log(`${request.client.username}: ${this.name} calibrated to ${rpm} rpm -> ${mlh} mlh`);
    } catch(e) {
      console.error(`${request.client.username}: Failed to calibrate ${this.name}: ${e.message}`);
      throw e;
    }        
  }    
  async RM_parallaxpump_setRPM(request, rpm) {    
    try {      
      await this.command(`SetRPM${this.__pumpid} ${rpm}`);
      this.rpm = rpm;
      console.log(`${request.client.username}: ${this.name} set to ${rpm} rpm`);
    } catch(e) {
      console.error(`${request.client.username}: Failed to set ${this.name} to ${rpm} rpm: ${e.message}`);
      throw e;
    }        
  }  
  async RM_parallaxpump_setMLH(request, mlh) {    
    try {
      await this.command(`SetMLH${this.__pumpid} ${mlh}`);
      this.mlh = mlh;
      console.log(`${request.client.username}: ${this.name} set to ${mlh} mlh`);
    } catch(e) {
      console.error(`${request.client.username}: Failed to set ${this.name} to ${mlh} mlh: ${e.message}`);
      throw e;
    }        
  }   
  async RM_parallaxpump_clearsum(request) {    
    try {
      this.clearSum();
      console.log(`${request.client.username}: ${this.name} sum cleared`);
    } catch(e) {
      console.error(e.message);
      throw e;
    }        
  }      
  __ontimeout() {
    this.sumML += (this.mlh/(60*60));
  }  
  logData(logData) {
    logData.append(`${this.name}_mlh`, this.mlh);
    logData.append(`${this.name}_ml`, this.sumML);
  }  
  toJSON() {
    return({
      id: this.id,
      usbProductName: this.usbProductName,
      usbSerialNumber: this.usbSerialNumber,            
      status: this.status,
      name: this.name,
      errors: this.errors,
      warnings: this.warnings,
      rpm: this.rpm,
      mlh: this.mlh,
      calibrationStatus: this.calibrationStatus,
      sumML: this.sumML,
      errorFlags: this.errorFlags
    });
  }
}

Module.constructor();

export default Module;